import React, { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

import Snackbar from '@mui/material/Snackbar';

import { IconSparkles } from '@tabler/icons-react';

import {
    IconSearch,
    IconPlus,
    IconBrandWhatsapp,
    IconMail,
    IconMessage2,
    IconCalendarEvent,
    IconUsers,
    IconSend,
    IconClock,
    IconTemplate,
    IconEye,
    IconDeviceFloppy,
    IconPlayerPlay,
    IconX,
    IconChevronRight,
    IconFileSpreadsheet,
    IconArrowLeft,
    IconUpload
} from '@tabler/icons';

import { motion } from 'framer-motion';
import axios from 'axios';
import api from '../../../../services/api';
import { useHistory, useParams } from 'react-router-dom';
import CampaignDetailDialog from './CampaignDetailDialog';
import TemplatePreviewCard from '../whatsapp_templates/TemplatePreviewCard';

const MotionCard = motion(Card);

// Agentes de IA externos (sistema chat_ia) - mesmo padrão de configuração
// já usado em `views/pages/authentication/configuracoes/agents/ViewAgents.js`.
const hostnameAgent = window.location.hostname;
let AGENT_SERVER;
if (hostnameAgent.startsWith('crm.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('glazzbeauty.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('casamathiola.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('bliveinpets.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('acin.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('braza55.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('po.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('florestavet.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('quimicer.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else if (hostnameAgent.startsWith('imobiliaria.')) {
    AGENT_SERVER = 'https://apichat.loged.com.br';
} else {
    AGENT_SERVER = 'http://127.0.0.1:7000';
}
const agentApi = axios.create({ baseURL: AGENT_SERVER });
agentApi.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const RESPONSE_ACTION_TYPES = [
    { value: 'opt_in_capture', label: 'Capturar opt-in/opt-out' },
    { value: 'ai_agent', label: 'Chamar agente de IA específico' },
    { value: 'human_queue', label: 'Transferir para fila humana' }
];

// Mesmos valores de `MIN_UNOFFICIAL_DELAY_SECONDS`/`MIN_OFFICIAL_DELAY_SECONDS`/
// `MAX_UNOFFICIAL_RECIPIENTS` em `conversas/tasks/campanhas/views.py` - o
// backend também aplica esses limites (aqui é só pra dar feedback
// imediato na tela, sem esperar o 400 do servidor).
const MIN_UNOFFICIAL_DELAY_SECONDS = 45;
const MIN_OFFICIAL_DELAY_SECONDS = 5;
const MAX_UNOFFICIAL_RECIPIENTS = 250;
// Mesma taxa usada no `send_campaign` pra estimar o pior caso (o delay de
// verdade é sorteado entre `delay` e `delay*1.5`).
const UNOFFICIAL_DELAY_JITTER_FACTOR = 1.5;
const OFFICIAL_BATCH_SIZE = 20;

const CAMPAIGN_STATUS_LABELS = {
    draft: 'Rascunho',
    scheduled: 'Agendada',
    processing: 'Enviando...',
    completed: 'Concluída',
    failed: 'Falhou'
};

const CAMPAIGN_STATUS_COLORS = {
    draft: 'default',
    scheduled: 'warning',
    processing: 'info',
    completed: 'success',
    failed: 'error'
};

const formatEstimatedDuration = (totalSeconds) => {
    if (totalSeconds < 60) return `${Math.ceil(totalSeconds)} segundos`;
    const totalMinutes = totalSeconds / 60;
    if (totalMinutes < 60) return `${Math.ceil(totalMinutes)} minuto(s)`;
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return minutes > 0 ? `${hours}h${minutes}min` : `${hours}h`;
};

// Rótulo da coluna de variável na planilha-modelo - nomeada pelo
// identificador real quando o template usa variável nomeada ({{nome}}),
// ou pela posição quando usa variável numérica ({{1}}) - mesma
// distinção que a Meta faz.
const campaignVarHeaderLabel = (varName) =>
    /^\d+$/.test(varName) ? `Variável {{${varName}}} (obrigatório)` : `Variável: ${varName} (obrigatório)`;

// Ignora acento/maiúscula/"(obrigatório)" na hora de casar um cabeçalho
// de volta com Nome/Telefone/E-mail/variável - pra planilha reimportada
// bater mesmo se a pessoa mudar a ordem das colunas ou o texto vier com
// capitalização diferente.
const normalizeCampaignHeader = (header) =>
    String(header || '')
        .replace(/\(obrigat[oó]rio\)/i, '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();

// O Excel trata um número de telefone longo digitado numa célula sem
// formato de Texto como um NÚMERO comum - com muitos dígitos, o formato
// "Geral" padrão vira notação científica (ex: "5.56799E+12" em vez de
// "5567992598698"). A planilha-modelo já sai pré-formatada como Texto
// pra evitar isso na origem (`handleDownloadCampaignTemplate`), mas essa
// função cobre arquivos antigos/editados por fora desse padrão.
const normalizePossibleScientificNotation = (value) => {
    const str = String(value ?? '').trim();
    if (/^\d+(\.\d+)?[eE]\+?\d+$/.test(str)) {
        const asNumber = Number(str);
        if (Number.isFinite(asNumber)) return String(Math.round(asNumber));
    }
    return str;
};

const CHANNELS = [
    {
        id: 'whatsapp',
        name: 'WhatsApp',
        icon: IconBrandWhatsapp,
        color: '#25D366'
    },

    {
        id: 'email',
        name: 'E-mail',
        icon: IconMail,
        color: '#2563eb'
    },

    {
        id: 'instagram',
        name: 'Instagram',
        icon: IconMessage2,
        color: '#E1306C'
    }
];

const DEFAULT_CAMPAIGN = {
    name: '',
    channel: 'whatsapp',
    integration_id: '',
    whatsapp_template_id: '',
    // Só usados quando channel === 'email' - não existe template pra
    // e-mail, o assunto/corpo são digitados direto na tela. `{{nome}}` no
    // corpo é substituído pelo nome de cada destinatário na hora do envio
    // (ver `send_campaign`, backend). Imagem/link são opcionais e viram
    // uma versão HTML do e-mail (a imagem só é enviada de fato depois que
    // a campanha já existe - `uploadEmailImage`, logo abaixo).
    email_subject: '',
    email_message: '',
    email_image_file: null,
    email_image_preview_url: '',
    email_link_url: '',
    email_link_text: '',
    schedule_date: '',
    schedule_time: '',
    // '' usa o default do sistema (backend aplica o piso mínimo do canal
    // de qualquer forma) - só preenchido se o usuário escolher um valor.
    send_delay_seconds: '',
    recipients: [],
    // Captura de resposta - se o cliente clicar num botão do template
    // (ex: opt-in/opt-out) ou responder de qualquer forma, o que fazer.
    capture_response: false,
    response_action_type: '',
    response_opt_in_text: '',
    response_opt_out_text: '',
    response_ai_agent_webhook_url: '',
    response_human_queue_id: ''
};

const MarketingCampaigns = () => {
    const theme = useTheme();
    const [loading, setLoading] = useState(true);
    const [openAlert, setOpenAlert] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [users, setUsers] = useState([]);
    const [search, setSearch] = useState('');
    const [userSearch, setUserSearch] = useState('');
    const [recipientMode, setRecipientMode] = useState('sistema');
    const [columnMapping, setColumnMapping] = useState({
        name: '',
        phone: '',
        email: ''
    });
    // Valores das variáveis do template, um POR DESTINATÁRIO (não um valor
    // único repetido pra campanha inteira) - chaveado por user.id no modo
    // "sistema" e pelo índice da linha bruta do CSV no modo "csv" (mesma
    // ordem que o PapaParse gera, pra bater com o que o backend espera em
    // `variables_by_row`). Formato de cada entrada: { header: '', body: [...] }.
    const [recipientVariables, setRecipientVariables] = useState({});
    const [previewData, setPreviewData] = useState([]);
    const [previewStats, setPreviewStats] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [uploadToken, setUploadToken] = useState(null);
    const [campaignId, setCampaignId] = useState(null);
    const [documentId, setDocumentId] = useState(null);
    const [csvRows, setCsvRows] = useState([]);
    const [csvFile, setCsvFile] = useState(null);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [csvUploaded, setCsvUploaded] = useState(false);
    // true quando a planilha reimportada já trouxe as colunas de variável
    // do template (geradas pela "Baixar planilha modelo") - nesse caso os
    // valores já vêm prontos por linha, sem precisar dos TextField manuais.
    const [variablesResolvedFromFile, setVariablesResolvedFromFile] = useState(false);
    const [status, setStatus] = useState({
        type: '',
        message: ''
    });
    const [tab, setTab] = useState(0);
    const [openCampaign, setOpenCampaign] = useState(false);
    // Campanha aberta no dashboard de detalhe (`CampaignDetailDialog`) -
    // só o id importa, o resto o próprio diálogo busca/repolla sozinho.
    const [detailCampaignId, setDetailCampaignId] = useState(null);
    const [campaignForm, setCampaignForm] = useState(DEFAULT_CAMPAIGN);
    const [uploading, setUploading] = useState(false);
    const [appId, setAppId] = useState(null);
    const [accountId, setAccountId] = useState(null);
    const [allIntegrations, setAllIntegrations] = useState([]);
    // Mesmo mecanismo (2 estados + merge) usado em ViewWhatsappTemplates.js
    // pra listar templates de uma integração - reaproveitado tal e qual
    // aqui, pra nunca divergir do que a tela "Templates WhatsApp" mostra.
    const [campaignMetaTemplates, setCampaignMetaTemplates] = useState([]);
    const [campaignLocalTemplates, setCampaignLocalTemplates] = useState([]);
    const [loadingCampaignTemplates, setLoadingCampaignTemplates] = useState(false);
    const [aiAgents, setAiAgents] = useState([]);
    const [switchboardIntegrations, setSwitchboardIntegrations] = useState([]);
    const history = useHistory();

    const pegar_app_padrao = async () => {
        try {
            const response = await api.get('v1/apps/default/');
            setAppId(response.data.app.id);
            setAccountId(response.data.app.account);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        pegar_app_padrao();
    }, []);

    // só lista quando tiver appId
    useEffect(() => {
        if (!appId) return;
        loadCampaign();
        loadUsers();
        loadSwitchboardIntegrations();
        loadIntegrations();
    }, [appId]);

    useEffect(() => {
        if (!appId || !accountId) return;
        loadAiAgents();
    }, [appId, accountId]);

    const loadCampaign = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/marketing/campaigns/`);
            setCampaigns(response.data?.campanhas || []);
            setLoading(false);
        } catch (error) {
            console.error(error);
        }
    };

    const loadIntegrations = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/integrations/`);
            const all = response.data.integrations || response.data || [];
            setAllIntegrations(all);
        } catch (error) {
            console.error('Erro ao carregar integrações', error);
        }
    };

    // Idêntico ao `loadTemplatesForIntegration` de ViewWhatsappTemplates.js
    // (mesmas 2 chamadas, mesmo merge por name+language) - reaproveitado tal
    // e qual aqui pra nunca divergir do que aquela tela mostra.
    const loadTemplatesForCampaignIntegration = async (integrationId, clientType) => {
        setLoadingCampaignTemplates(true);
        try {
            const requests = [api.get(`v1/apps/${appId}/whatsapp_templates/?integration_id=${integrationId}`)];
            if (clientType === 'official') {
                requests.unshift(api.get(`v1/apps/${appId}/integrations/${integrationId}/whatsapp_templates_meta/`));
            }

            const results = await Promise.all(requests);
            const localRes = results[results.length - 1];
            const metaRes = clientType === 'official' ? results[0] : null;

            setCampaignMetaTemplates(metaRes && Array.isArray(metaRes.data) ? metaRes.data : []);
            setCampaignLocalTemplates(localRes.data || []);
        } catch (err) {
            console.error('Erro ao carregar templates pra campanha', err);
            setCampaignMetaTemplates([]);
            setCampaignLocalTemplates([]);
        } finally {
            setLoadingCampaignTemplates(false);
        }
    };

    const loadSwitchboardIntegrations = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/switchboardIntegrations/`);
            setSwitchboardIntegrations(response.data?.switchboardIntegrations || []);
        } catch (error) {
            console.error('Erro ao carregar filas/respondedores', error);
        }
    };

    const loadAiAgents = async () => {
        try {
            const response = await agentApi.get(`/agent/api/v1/account/${accountId}/app/${appId}/agents/`);
            setAiAgents(response.data?.agents || []);
        } catch (error) {
            console.error('Erro ao carregar agentes de IA', error);
        }
    };

    const loadUsers = async () => {
        try {
            const response = await api.get(`v1/apps/${appId}/users/`);
            setUsers(response.data?.users || []);
            setLoading(false);
        } catch (error) {
            console.error(error);
        }
    };

    const filteredCampaigns = useMemo(() => {
        return campaigns.filter((campaign) => campaign.title?.toLowerCase().includes(search.toLowerCase()));
    }, [campaigns, search]);

    // Integrações disponíveis pro canal escolhido na campanha - e-mail usa
    // Integration.type='email', WhatsApp usa client_type oficial/w-api
    // (mesmo filtro que já existia antes, só que agora reativo ao canal).
    const channelIntegrations = useMemo(() => {
        if (campaignForm.channel === 'email') {
            return allIntegrations.filter((i) => i.type === 'email');
        }
        return allIntegrations.filter((i) => i.client_type === 'official' || i.client_type === 'w_api');
    }, [allIntegrations, campaignForm.channel]);

    const filteredUsers = useMemo(() => {
        return users.filter(
            (user) =>
                user.name?.toLowerCase().includes(userSearch.toLowerCase()) || user.email?.toLowerCase().includes(userSearch.toLowerCase())
        );
    }, [users, userSearch]);

    const handleToggleRecipient = (user) => {
        const exists = campaignForm.recipients.find((u) => u.id === user.id);

        if (exists) {
            setCampaignForm({
                ...campaignForm,
                recipients: campaignForm.recipients.filter((u) => u.id !== user.id)
            });
            setRecipientVariables((prev) => {
                const next = { ...prev };
                delete next[user.id];
                return next;
            });

            return;
        }

        setCampaignForm({
            ...campaignForm,
            recipients: [...campaignForm.recipients, user]
        });
        const bodyCount = contarVariaveisCampanha(selectedCampaignTemplateFields?.bodyText);
        setRecipientVariables((prev) => ({
            ...prev,
            [user.id]: { header: '', body: new Array(bodyCount).fill('') }
        }));
    };

    const createCampaign = async () => {
        // O backend ainda exige um `message` não-vazio (usado como texto de
        // fallback/preview da campanha) - pra WhatsApp preenche sozinho a
        // partir do template selecionado (não existe campo de mensagem
        // livre nessa tela pra esse canal); pra e-mail usa o texto digitado
        // no campo "Mensagem", já que não existe template.
        const selectedTemplate = campaignTemplateEntries.find((entry) => entry.local?.id === campaignForm.whatsapp_template_id)?.local;
        const message =
            campaignForm.channel === 'email'
                ? campaignForm.email_message
                : selectedTemplate?.body_text || selectedTemplate?.name || campaignForm.name;

        // O backend exige `scheduled_date` (data + hora) - antes só a data
        // era mandada (a hora escolhida era descartada) e, se a data
        // ficasse em branco, dava 400 sem nenhum aviso na tela.
        //
        // O backend roda com TIME_ZONE=UTC - mandar a string "ingênua" (sem
        // fuso, ex: "2026-08-04T22:00:00") faz o Django tratar esse horário
        // como se já fosse UTC, e não o horário local do navegador. Como o
        // Brasil está atrás de UTC, um horário "futuro" escolhido aqui podia
        // virar um instante já passado em UTC, disparando na hora em vez de
        // agendar. `new Date(...)` interpreta a string sem fuso como
        // horário LOCAL do navegador (comportamento padrão do JS) e
        // `.toISOString()` converte isso pro instante UTC certo.
        const scheduledDate = new Date(`${campaignForm.schedule_date}T${campaignForm.schedule_time || '00:00'}:00`).toISOString();

        const response = await api.post(`v1/apps/${appId}/marketing/campaigns/`, {
            title: campaignForm.name,
            channel: campaignForm.channel,
            message,
            subject: campaignForm.channel === 'email' ? campaignForm.email_subject || null : null,
            email_link_url: campaignForm.channel === 'email' ? campaignForm.email_link_url || null : null,
            email_link_text: campaignForm.channel === 'email' ? campaignForm.email_link_text || null : null,
            integration_id: campaignForm.integration_id || null,
            scheduled_date: scheduledDate,
            status: 'draft',
            send_delay_seconds: campaignForm.send_delay_seconds !== '' ? Number(campaignForm.send_delay_seconds) : null,
            whatsapp_template_id: campaignForm.whatsapp_template_id || null,
            capture_response: campaignForm.capture_response,
            response_action_type: campaignForm.capture_response ? campaignForm.response_action_type || null : null,
            response_opt_in_text: campaignForm.response_opt_in_text || null,
            response_opt_out_text: campaignForm.response_opt_out_text || null,
            response_ai_agent_webhook_url: campaignForm.response_ai_agent_webhook_url || null,
            response_human_queue_id: campaignForm.response_human_queue_id || null
        });

        return response.data;
    };

    const uploadDocument = async (campaignId) => {
        if (!csvFile) return null;

        const formData = new FormData();

        formData.append('file', csvFile);

        formData.append('total_rows', csvRows.length);

        formData.append('valid_rows', csvRows.length);

        formData.append('invalid_rows', 0);

        const response = await api.post(`v1/apps/${appId}/marketing/campaigns/${campaignId}/document/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });

        return response.data;
    };

    const uploadEmailImage = async (campaignId) => {
        if (!campaignForm.email_image_file) return null;

        const formData = new FormData();
        formData.append('image', campaignForm.email_image_file);

        const response = await api.post(`v1/apps/${appId}/marketing/campaigns/${campaignId}/email_image/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });

        return response.data;
    };

    const saveColumns = async (campaignId) => {
        return api.post(`v1/apps/${appId}/marketing/campaigns/${campaignId}/columns/`, {
            mapping: {
                nome: columnMapping.name,
                telefone: columnMapping.phone,
                email: columnMapping.email
            }
        });
    };

    // sendNow=true (botão "Salvar Campanha") manda na hora, ignorando a data
    // agendada. sendNow=false (botão "Agendar Disparo") só cria os
    // destinatários agora e deixa a task periódica `trigger_campaign`
    // disparar quando a data/hora escolhida chegar.
    const handleSaveCampaign = async (sendNow) => {
        try {
            setUploading(true);
            setStatus({ type: '', message: '' });

            if (recipientMode === 'sistema' && campaignForm.recipients.length === 0) {
                throw new Error('Selecione pelo menos um contato do sistema');
            }
            if (recipientMode === 'csv' && !csvFile) {
                throw new Error('Selecione uma planilha com os destinatários');
            }
            if (!sendNow) {
                const scheduledAt = new Date(`${campaignForm.schedule_date}T${campaignForm.schedule_time || '00:00'}:00`);
                if (scheduledAt <= new Date()) {
                    throw new Error('Escolha uma data/hora futura pra agendar o disparo');
                }
            }

            // 1 - cria campanha
            const campaignResponse = await createCampaign();

            const campaignId =
                campaignResponse.campaign?.id || campaignResponse.campaign_id || campaignResponse.campanha || campaignResponse.id;

            if (!campaignId) {
                throw new Error('Não foi possível obter o ID da campanha');
            }

            // 2 - upload csv
            if (csvFile) {
                await uploadDocument(campaignId);
            }

            // 2b - upload da imagem do e-mail (se escolhida)
            if (campaignForm.channel === 'email' && campaignForm.email_image_file) {
                await uploadEmailImage(campaignId);
            }

            // 3 - salva mapeamento
            if (columnMapping.name && (columnMapping.phone || columnMapping.email)) {
                await saveColumns(campaignId);
            }

            // 4 - backend cria recipients + queues e manda na hora ou agenda
            await handleProcessCampaign(campaignId, sendNow);

            setOpenCampaign(false);

            if (campaignForm.email_image_preview_url) URL.revokeObjectURL(campaignForm.email_image_preview_url);

            setCampaignForm(DEFAULT_CAMPAIGN);

            setCsvRows([]);

            setCsvHeaders([]);

            setCsvFile(null);

            setColumnMapping({
                name: '',
                phone: '',
                email: ''
            });

            setRecipientVariables({});

            setVariablesResolvedFromFile(false);

            setOpenAlert(true);
        } catch (error) {
            console.error(error);

            setStatus({
                type: 'error',
                message: error.response?.data?.error || error.message || 'Erro ao criar campanha.'
            });
        } finally {
            setUploading(false);
        }
    };

    // Destinatários da planilha, no mesmo formato que `process-recipients/`
    // já aceita pro modo "Contatos do sistema" - linhas sem telefone/e-mail
    // são descartadas aqui (o backend faz o mesmo, mas assim o contador que
    // aparece na tela bate com o que de fato vai ser enviado).
    const buildCsvRecipientsPayload = () => {
        const isEmailCampaign = campaignForm.channel === 'email';
        return csvRows
            .map((row, index) => {
                const phone = columnMapping.phone ? normalizePossibleScientificNotation(row[columnMapping.phone]) : '';
                const email = columnMapping.email ? (row[columnMapping.email] || '').toString().trim() : '';
                const contato = isEmailCampaign ? email : phone;
                if (!contato) return null;

                const nome = columnMapping.name ? (row[columnMapping.name] || '').toString().trim() : '';
                const vars = recipientVariables[index];
                const recipient = { name: nome };
                if (isEmailCampaign) {
                    recipient.email = contato;
                } else {
                    recipient.phone = contato;
                    recipient.template_header_variable = vars?.header || null;
                    recipient.template_body_variables = vars?.body || [];
                }
                return recipient;
            })
            .filter(Boolean);
    };

    const handleProcessCampaign = async (campaignId, sendNow) => {
        const recipients =
            recipientMode === 'sistema'
                ? campaignForm.recipients.map((u) => ({
                      name: u.name,
                      phone: u.phone,
                      email: u.email,
                      template_header_variable: recipientVariables[u.id]?.header || null,
                      template_body_variables: recipientVariables[u.id]?.body || []
                  }))
                : buildCsvRecipientsPayload();

        await api.post(`v1/apps/${appId}/marketing/campaigns/${campaignId}/process-recipients/`, {
            recipients,
            send_now: sendNow
        });

        setStatus({
            type: 'success',
            message: sendNow ? 'Campanha criada e enviada para processamento.' : 'Campanha agendada com sucesso.'
        });

        loadCampaign();
    };

    // Baixa uma planilha-modelo com as colunas certas pro template
    // selecionado - Nome/Telefone(ou E-mail) + uma coluna por variável do
    // cabeçalho e do corpo, já com uma linha de exemplo. Mesmo padrão de
    // geração usado em ViewOrganization.js (XLSX.utils.aoa_to_sheet +
    // XLSX.writeFile), sem endpoint novo no backend.
    const handleDownloadCampaignTemplate = () => {
        const isEmailCampaign = campaignForm.channel === 'email';
        const headers = ['Nome', isEmailCampaign ? 'E-mail (obrigatório)' : 'Telefone (obrigatório)'];
        const exampleRow = ['João da Silva', isEmailCampaign ? 'joao@exemplo.com' : '5511999999999'];

        const local = selectedCampaignTemplateEntry?.local;
        if (campaignHeaderVarNames.length > 0) {
            headers.push(campaignVarHeaderLabel(campaignHeaderVarNames[0]));
            exampleRow.push(local?.header_example_value || '');
        }
        campaignBodyVarNames.forEach((varName, i) => {
            headers.push(campaignVarHeaderLabel(varName));
            exampleRow.push(local?.body_example_values?.[i] || '');
        });

        const sheet = XLSX.utils.aoa_to_sheet([headers, exampleRow]);

        // Pré-formata a coluna de Telefone/E-mail como Texto (formato "@")
        // numa faixa generosa de linhas - sem isso, o Excel trata um
        // telefone longo digitado numa célula em branco como NÚMERO
        // comum, e o formato "Geral" padrão vira notação científica (ex:
        // "5.56799E+12" em vez de "5567992598698") assim que a pessoa
        // preenche a planilha. Precisa "pré-existir" a célula (mesmo
        // vazia) pra o Excel respeitar esse formato quando o valor for
        // digitado depois - por isso cria as células em branco aqui, não
        // só a de exemplo.
        const CONTACT_COLUMN_INDEX = 1;
        const PREFORMAT_ROWS = 1000;
        for (let row = 0; row < PREFORMAT_ROWS; row++) {
            const addr = XLSX.utils.encode_cell({ r: row, c: CONTACT_COLUMN_INDEX });
            if (sheet[addr]) {
                sheet[addr].z = '@';
                if (typeof sheet[addr].v === 'number') {
                    sheet[addr].t = 's';
                    sheet[addr].v = String(sheet[addr].v);
                }
            } else {
                sheet[addr] = { t: 's', v: '', z: '@' };
            }
        }
        const range = XLSX.utils.decode_range(sheet['!ref']);
        range.e.r = Math.max(range.e.r, PREFORMAT_ROWS - 1);
        sheet['!ref'] = XLSX.utils.encode_range(range);

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, sheet, 'Destinatários');
        XLSX.writeFile(workbook, `modelo_campanha_${campaignForm.name || 'destinatarios'}.xlsx`);
    };

    const handleCsvUpload = async (event) => {
        const file = event.target.files[0];

        if (!file) return;

        setCsvFile(file);
        // Um arquivo novo muda o que cada índice de linha significa - os
        // valores de variável guardados pro arquivo anterior não fazem mais
        // sentido.
        setRecipientVariables({});
        setColumnMapping({ name: '', phone: '', email: '' });
        setVariablesResolvedFromFile(false);

        try {
            const buffer = await file.arrayBuffer();
            const workbook = XLSX.read(buffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[firstSheetName];
            // `raw: true` + stringificação manual abaixo, não `raw: false` -
            // pra uma célula NUMÉRICA (ex: telefone digitado numa célula
            // sem o formato de Texto de `handleDownloadCampaignTemplate`,
            // caso de um arquivo mais antigo), `raw: false` usa o texto já
            // FORMATADO da célula (o mesmo que o Excel mostra), que pra
            // número grande em formato "Geral" já vem em notação
            // científica arredondada (ex: "5.56799E+12") - perde dígito de
            // verdade, sem volta. O valor NUMÉRICO bruto da célula (`raw:
            // true`) continua com precisão total; `String(numero)` do JS
            // só usa notação científica a partir de 1e21 (bem acima de
            // qualquer telefone), então nunca teria esse problema.
            const rows = XLSX.utils.sheet_to_json(sheet, { defval: '', raw: true }).map((row) => {
                const normalized = {};
                Object.entries(row).forEach(([key, value]) => {
                    normalized[key] = typeof value === 'number' ? String(value) : value;
                });
                return normalized;
            });

            setCsvRows(rows);
            const headers = Object.keys(rows[0] || {});
            setCsvHeaders(headers);

            // Tenta casar automaticamente Nome/Telefone/E-mail e as colunas
            // de variável (geradas por "Baixar planilha modelo") pelo texto
            // do cabeçalho - se achar, já preenche tudo sem precisar de
            // digitação manual linha a linha.
            const headerVarLabel =
                campaignHeaderVarNames.length > 0 ? normalizeCampaignHeader(campaignVarHeaderLabel(campaignHeaderVarNames[0])) : null;
            const bodyVarLabels = campaignBodyVarNames.map((varName) => normalizeCampaignHeader(campaignVarHeaderLabel(varName)));

            let nameHeader = null;
            let phoneHeader = null;
            let emailHeader = null;
            let headerVarHeader = null;
            const bodyVarHeaders = new Array(campaignBodyVarNames.length).fill(null);

            headers.forEach((header) => {
                const normalized = normalizeCampaignHeader(header);
                if (normalized === 'nome') nameHeader = header;
                else if (normalized === 'telefone') phoneHeader = header;
                else if (normalized === 'e-mail' || normalized === 'email') emailHeader = header;
                else if (headerVarLabel && normalized === headerVarLabel) headerVarHeader = header;
                else {
                    const bodyIdx = bodyVarLabels.indexOf(normalized);
                    if (bodyIdx !== -1) bodyVarHeaders[bodyIdx] = header;
                }
            });

            if (nameHeader || phoneHeader || emailHeader) {
                setColumnMapping({ name: nameHeader || '', phone: phoneHeader || '', email: emailHeader || '' });
            }

            const achouTodasVariaveis =
                (campaignHeaderVarNames.length === 0 || !!headerVarHeader) &&
                (campaignBodyVarNames.length === 0 || bodyVarHeaders.every(Boolean)) &&
                (campaignHeaderVarNames.length > 0 || campaignBodyVarNames.length > 0);

            if (achouTodasVariaveis) {
                const nextVars = {};
                rows.forEach((row, index) => {
                    nextVars[index] = {
                        header: headerVarHeader ? String(row[headerVarHeader] ?? '').trim() : '',
                        body: bodyVarHeaders.map((h) => (h ? String(row[h] ?? '').trim() : ''))
                    };
                });
                setRecipientVariables(nextVars);
                setVariablesResolvedFromFile(true);
            }
        } catch (err) {
            console.error('Erro ao ler planilha', err);
            setStatus({
                type: 'error',
                message: 'Não foi possível ler o arquivo. Confirme que é uma planilha .xlsx, .xls ou .csv válida.'
            });
        }
    };

    const handleClickAlert = () => {
        setOpenAlert(true);
    };

    const handleCloseAlert = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenAlert(false);
    };

    // Merge idêntico ao de ViewWhatsappTemplates.js: junta os templates da
    // Meta com os locais por name+language - só templates com uma linha
    // local (entry.local) podem ser referenciados pela campanha (é o `id`
    // que ela precisa).
    const campaignLocalByKey = {};
    campaignLocalTemplates.forEach((t) => {
        campaignLocalByKey[`${t.name}__${t.language}`] = t;
    });
    const campaignMatchedKeys = new Set();
    const campaignEntriesFromMeta = campaignMetaTemplates.map((mt) => {
        const key = `${mt.name}__${mt.language}`;
        const local = campaignLocalByKey[key] || null;
        if (local) campaignMatchedKeys.add(key);
        return { meta: mt, local };
    });
    const campaignEntriesLocalOnly = campaignLocalTemplates
        .filter((t) => !campaignMatchedKeys.has(`${t.name}__${t.language}`) && !t.meta_template_id)
        .map((t) => ({ meta: null, local: t }));
    const campaignTemplateEntries = [...campaignEntriesFromMeta, ...campaignEntriesLocalOnly];

    // A Meta suporta variável numérica ({{1}}) e nomeada ({{nome}}) - conta
    // qualquer coisa dentro de {{...}}, não só números.
    const contarVariaveisCampanha = (texto) => (texto?.match(/\{\{[^}]+\}\}/g) || []).length;

    // Extrai os NOMES das variáveis (ex: "nome", "data"), na ordem em que
    // aparecem no texto - usado pra rotular cada input com o nome real da
    // variável em vez de um rótulo genérico tipo "Variável {{1}}". Mesmo
    // regex usado no backend (extract_variables em templates.py).
    const extrairNomesVariaveisCampanha = (texto) => {
        if (!texto) return [];
        return [...texto.matchAll(/\{\{\s*([^}]+?)\s*\}\}/g)].map((m) => m[1]);
    };

    // Mesma lógica de "Meta é a fonte real de verdade" já usada na tela de
    // Chats - prefere o componente vindo da Meta e cai pro cache local
    // campo a campo se a Meta não tiver esse componente específico.
    const getCampaignTemplateFields = (entry) => {
        const components = entry?.meta?.components || [];
        const getComponentText = (type) => components.find((c) => c.type === type)?.text;
        return {
            headerText: getComponentText('HEADER') ?? entry?.local?.header_text,
            bodyText: getComponentText('BODY') ?? entry?.local?.body_text,
            footerText: getComponentText('FOOTER') ?? entry?.local?.footer_text
        };
    };

    const selectedCampaignTemplateEntry = campaignTemplateEntries.find((entry) => entry.local?.id === campaignForm.whatsapp_template_id);
    const selectedCampaignTemplateFields = selectedCampaignTemplateEntry ? getCampaignTemplateFields(selectedCampaignTemplateEntry) : null;

    const campaignHeaderVarNames = extrairNomesVariaveisCampanha(selectedCampaignTemplateFields?.headerText);
    const campaignBodyVarNames = extrairNomesVariaveisCampanha(selectedCampaignTemplateFields?.bodyText);

    // true se algum destinatário (que de fato vai ser enviado - linha de
    // CSV sem telefone não conta, ela é pulada no backend) ainda não tem
    // todas as variáveis do template preenchidas.
    const algumDestinatarioSemVariavel = (() => {
        if (campaignForm.channel !== 'whatsapp' || !selectedCampaignTemplateEntry) return false;
        if (campaignHeaderVarNames.length === 0 && campaignBodyVarNames.length === 0) return false;

        const faltaPreencher = (vars) => {
            if (!vars) return true;
            if (campaignHeaderVarNames.length > 0 && !vars.header) return true;
            return campaignBodyVarNames.some((_, i) => !vars.body?.[i]);
        };

        if (recipientMode === 'sistema') {
            return campaignForm.recipients.some((user) => faltaPreencher(recipientVariables[user.id]));
        }

        if (recipientMode === 'csv' && columnMapping.phone) {
            return csvRows.some((row, index) => {
                const phone = normalizePossibleScientificNotation(row[columnMapping.phone]);
                if (!phone) return false;
                return faltaPreencher(recipientVariables[index]);
            });
        }

        return false;
    })();

    // Integração escolhida no formulário - decide o piso do delay (API
    // Oficial x não-oficial) e se o limite de 250 destinatários se aplica.
    const selectedFormIntegration = channelIntegrations.find((i) => i.id === campaignForm.integration_id);
    const isUnofficialWhatsappCampaign =
        campaignForm.channel === 'whatsapp' && selectedFormIntegration && selectedFormIntegration.client_type !== 'official';
    const minDelaySeconds =
        campaignForm.channel === 'whatsapp'
            ? isUnofficialWhatsappCampaign
                ? MIN_UNOFFICIAL_DELAY_SECONDS
                : MIN_OFFICIAL_DELAY_SECONDS
            : null;

    // Quantidade real de destinatários que de fato vão receber (mesma
    // contagem que o backend usa - CSV descarta linha sem telefone/e-mail).
    const recipientCount = recipientMode === 'sistema' ? campaignForm.recipients.length : buildCsvRecipientsPayload().length;

    const excedeuLimiteNaoOficial = isUnofficialWhatsappCampaign && recipientCount > MAX_UNOFFICIAL_RECIPIENTS;

    // Estimativa de tempo total - pior caso (delay*jitter) pra
    // não-oficial, já que é 1 por vez; lotes de `OFFICIAL_BATCH_SIZE`
    // pra Oficial (bem mais rápido, tolera volume).
    const estimatedTotalSeconds = (() => {
        if (campaignForm.channel !== 'whatsapp' || recipientCount === 0) return null;
        const delay = Number(campaignForm.send_delay_seconds) || minDelaySeconds || MIN_OFFICIAL_DELAY_SECONDS;
        if (isUnofficialWhatsappCampaign) {
            return recipientCount * delay * UNOFFICIAL_DELAY_JITTER_FACTOR;
        }
        return Math.ceil(recipientCount / OFFICIAL_BATCH_SIZE) * delay;
    })();

    // Condições comuns aos dois botões (Salvar Campanha / Agendar Disparo) -
    // "Agendar Disparo" ainda soma a checagem de data futura por cima disso.
    const campaignSaveDisabled =
        uploading ||
        !campaignForm.name ||
        !campaignForm.schedule_date ||
        (campaignForm.channel === 'whatsapp' && !campaignForm.whatsapp_template_id) ||
        (campaignForm.channel === 'email' && !campaignForm.integration_id) ||
        algumDestinatarioSemVariavel ||
        (recipientMode === 'sistema' && campaignForm.recipients.length === 0) ||
        (recipientMode === 'csv' && !csvFile) ||
        excedeuLimiteNaoOficial;

    const handleSelectCampaignTemplate = (templateId) => {
        const entry = campaignTemplateEntries.find((e) => e.local?.id === templateId);
        const { bodyText } = getCampaignTemplateFields(entry);
        const bodyCount = contarVariaveisCampanha(bodyText);
        setCampaignForm({ ...campaignForm, whatsapp_template_id: templateId });
        // Template diferente pode ter variáveis diferentes (quantidade e
        // nomes) - os valores já preenchidos pros destinatários atuais não
        // fazem mais sentido, mas mantém as CHAVES (quem já tava
        // selecionado continua selecionado) só limpando os valores.
        setRecipientVariables((prev) => {
            const next = {};
            Object.keys(prev).forEach((key) => {
                next[key] = { header: '', body: new Array(bodyCount).fill('') };
            });
            return next;
        });
        // Template diferente = colunas de variável diferentes - os valores
        // que vieram auto-preenchidos da planilha anterior não valem mais
        // pro novo template.
        setVariablesResolvedFromFile(false);
    };

    const updateRecipientHeaderVar = (key, value) => {
        setRecipientVariables((prev) => ({
            ...prev,
            [key]: { header: value, body: prev[key]?.body || new Array(campaignBodyVarNames.length).fill('') }
        }));
    };

    const updateRecipientBodyVar = (key, index, value) => {
        setRecipientVariables((prev) => {
            const atual = prev[key] || { header: '', body: new Array(campaignBodyVarNames.length).fill('') };
            const body = [...atual.body];
            body[index] = value;
            return { ...prev, [key]: { ...atual, body } };
        });
    };

    return (
        <Box>
            {/* HERO */}
            <Button
                variant="outlined"
                startIcon={<IconArrowLeft size={18} />}
                onClick={() => history.goBack()}
                sx={{
                    borderRadius: '12px',
                    textTransform: 'none'
                }}
            >
                Voltar
            </Button>

            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderRadius: '34px',
                    marginTop: 2,
                    p: {
                        xs: 3,
                        md: 5
                    },
                    mb: 4,
                    background:
                        theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg,#111827 0%,#0f172a 100%)'
                            : 'linear-gradient(135deg,#f8fafc 0%,#ffffff 100%)',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 10px 40px rgba(0,0,0,0.35)' : '0 10px 40px rgba(15,23,42,0.06)'
                }}
            >
                <Box
                    sx={{
                        position: 'absolute',
                        width: 340,
                        height: 340,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(37,99,235,0.18), rgba(124,58,237,0.16))',
                        top: -120,
                        right: -100,
                        filter: 'blur(60px)'
                    }}
                />

                <Box
                    sx={{
                        position: 'relative',
                        zIndex: 2
                    }}
                >
                    <Chip
                        icon={<IconSparkles size={14} />}
                        label="Marketing Inteligente"
                        sx={{
                            mb: 2,
                            fontWeight: 700
                        }}
                    />

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 900,
                            fontSize: {
                                xs: '2rem',
                                md: '3rem'
                            },
                            letterSpacing: '-2px',
                            mb: 1,
                            background: 'linear-gradient(90deg,#2563eb,#7c3aed)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}
                    >
                        Campanhas de Marketing
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: 'text.secondary',
                            maxWidth: 760,
                            lineHeight: 1.8,
                            mb: 4
                        }}
                    >
                        Crie campanhas pró-ativas com templates, mídia, botões dinâmicos, agendamento e disparo inteligente para WhatsApp,
                        Instagram e e-mail.
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <OutlinedInput
                                fullWidth
                                placeholder="Buscar campanhas..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startAdornment={
                                    <InputAdornment position="start">
                                        <IconSearch size={18} />
                                    </InputAdornment>
                                }
                                sx={{
                                    height: 58,
                                    borderRadius: '18px',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff'
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={<IconPlus size={18} />}
                                onClick={() => setOpenCampaign(true)}
                                sx={{
                                    height: 58,
                                    borderRadius: '18px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                                }}
                            >
                                Criar Nova Campanha
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Box>

            {openAlert && (
                <Snackbar
                    open={openAlert}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseAlert} severity="success" variant="filled" sx={{ width: '100%' }}>
                        Campanha criada com sucesso!
                    </Alert>
                </Snackbar>
            )}

            {/* TABS */}

            <Tabs
                value={tab}
                onChange={(_, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                    mb: 4
                }}
            >
                <Tab label="Campanhas" />

                <Tab label="Agendadas" />

                <Tab label="Finalizadas" />
            </Tabs>

            {/* CONTENT */}

            {loading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        py: 10
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredCampaigns.map((campaign, index) => {
                        const channel = CHANNELS.find((c) => c.id === campaign.channel);

                        const ChannelIcon = channel?.icon || IconMessage2;

                        return (
                            <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                                <MotionCard
                                    initial={{
                                        opacity: 0,
                                        y: 20
                                    }}
                                    animate={{
                                        opacity: 1,
                                        y: 0
                                    }}
                                    transition={{
                                        delay: index * 0.04
                                    }}
                                    whileHover={{
                                        y: -5
                                    }}
                                    sx={{
                                        borderRadius: '26px',
                                        overflow: 'hidden',
                                        border:
                                            theme.palette.mode === 'dark'
                                                ? '1px solid rgba(255,255,255,0.06)'
                                                : '1px solid rgba(15,23,42,0.06)',
                                        background: theme.palette.mode === 'dark' ? '#111827' : '#fff'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: 160,
                                            backgroundImage: `url(${campaign.image})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            position: 'relative'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                inset: 0,
                                                background: 'linear-gradient(180deg,rgba(0,0,0,0.1),rgba(0,0,0,0.6))'
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 18,
                                                left: 18
                                            }}
                                        >
                                            <Chip
                                                icon={<ChannelIcon size={14} />}
                                                label={channel?.name}
                                                sx={{
                                                    background: '#fff',
                                                    fontWeight: 700
                                                }}
                                            />
                                        </Box>
                                    </Box>

                                    <CardContent
                                        sx={{
                                            p: 3
                                        }}
                                    >
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 800,
                                                mb: 1
                                            }}
                                        >
                                            {campaign.title}
                                        </Typography>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: 'text.secondary',
                                                mb: 2,
                                                lineHeight: 1.7
                                            }}
                                        >
                                            {campaign.message}
                                        </Typography>

                                        <Divider
                                            sx={{
                                                my: 2
                                            }}
                                        />

                                        <Stack spacing={1.5}>
                                            {(() => {
                                                const total = campaign.total_recipients || 0;
                                                const sent = campaign.sent_count || 0;
                                                const failed = campaign.failed_count || 0;
                                                const progressPercent =
                                                    total > 0 ? Math.min(100, Math.round(((sent + failed) / total) * 100)) : 0;
                                                return (
                                                    <>
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between'
                                                            }}
                                                        >
                                                            <Typography variant="body2">Progresso</Typography>

                                                            <Typography variant="body2">{progressPercent}%</Typography>
                                                        </Box>

                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progressPercent}
                                                            sx={{
                                                                height: 10,
                                                                borderRadius: '999px'
                                                            }}
                                                        />
                                                    </>
                                                );
                                            })()}

                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: 'text.secondary'
                                                    }}
                                                >
                                                    {campaign.total_recipients} destinatários - {campaign.sent_count || 0} enviados
                                                    {campaign.failed_count ? `, ${campaign.failed_count} falharam` : ''}
                                                </Typography>

                                                <Chip
                                                    label={CAMPAIGN_STATUS_LABELS[campaign.status] || campaign.status}
                                                    size="small"
                                                    color={CAMPAIGN_STATUS_COLORS[campaign.status] || 'default'}
                                                />
                                            </Box>
                                        </Stack>

                                        <Divider
                                            sx={{
                                                my: 2
                                            }}
                                        />

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <Button
                                                startIcon={<IconEye size={16} />}
                                                onClick={() => setDetailCampaignId(campaign.id)}
                                                sx={{
                                                    textTransform: 'none',
                                                    borderRadius: '14px'
                                                }}
                                            >
                                                Visualizar
                                            </Button>

                                            <IconChevronRight size={18} />
                                        </Box>
                                    </CardContent>
                                </MotionCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            {/* CREATE CAMPAIGN */}

            <Dialog open={openCampaign} onClose={() => setOpenCampaign(false)} fullScreen>
                <Box
                    sx={{
                        display: 'flex',
                        height: '100%',
                        background: theme.palette.mode === 'dark' ? '#0f172a' : '#f8fafc'
                    }}
                >
                    {/* LEFT */}

                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 4
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 4
                            }}
                        >
                            <Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<IconArrowLeft size={18} />}
                                    onClick={() => setOpenCampaign(false)}
                                    sx={{
                                        borderRadius: '12px',
                                        textTransform: 'none'
                                    }}
                                >
                                    Voltar
                                </Button>
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 900,
                                        mt: 2
                                    }}
                                >
                                    Criar Campanha
                                </Typography>

                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: 'text.secondary'
                                    }}
                                >
                                    Configure sua template pró-ativa
                                </Typography>
                            </Box>

                            <IconButton onClick={() => setOpenCampaign(false)}>
                                <IconX />
                            </IconButton>
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Nome da campanha"
                                    value={campaignForm.name}
                                    onChange={(e) =>
                                        setCampaignForm({
                                            ...campaignForm,
                                            name: e.target.value
                                        })
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Canal"
                                    value={campaignForm.channel}
                                    onChange={(e) =>
                                        setCampaignForm({
                                            ...campaignForm,
                                            channel: e.target.value
                                        })
                                    }
                                >
                                    {CHANNELS.map((channel) => (
                                        <MenuItem key={channel.id} value={channel.id}>
                                            {channel.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            {(campaignForm.channel === 'whatsapp' || campaignForm.channel === 'email') && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Integração"
                                        helperText={
                                            campaignForm.channel === 'email' && channelIntegrations.length > 1
                                                ? 'Escolha de qual canal de e-mail cadastrado esse disparo vai sair'
                                                : undefined
                                        }
                                        value={campaignForm.integration_id}
                                        onChange={(e) => {
                                            const integrationId = e.target.value;
                                            setCampaignForm({
                                                ...campaignForm,
                                                integration_id: integrationId,
                                                whatsapp_template_id: ''
                                            });
                                            if (campaignForm.channel === 'whatsapp') {
                                                setRecipientVariables({});
                                                setVariablesResolvedFromFile(false);
                                                setCampaignMetaTemplates([]);
                                                setCampaignLocalTemplates([]);
                                                const integration = channelIntegrations.find((i) => i.id === integrationId);
                                                if (integration) {
                                                    loadTemplatesForCampaignIntegration(integrationId, integration.client_type);
                                                }
                                            }
                                        }}
                                    >
                                        {channelIntegrations.map((integration) => (
                                            <MenuItem key={integration.id} value={integration.id}>
                                                {integration.displayName}
                                                {campaignForm.channel === 'whatsapp' &&
                                                    ` ${integration.client_type === 'w_api' ? 'Não Oficial' : '(Oficial)'}`}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                            )}

                            {campaignForm.channel === 'whatsapp' && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Template"
                                        disabled={!campaignForm.integration_id || loadingCampaignTemplates}
                                        helperText={
                                            !campaignForm.integration_id
                                                ? 'Selecione a integração primeiro'
                                                : loadingCampaignTemplates
                                                  ? 'Carregando templates...'
                                                  : 'Criado na tela "Templates WhatsApp"'
                                        }
                                        value={campaignForm.whatsapp_template_id}
                                        onChange={(e) => handleSelectCampaignTemplate(e.target.value)}
                                    >
                                        {campaignTemplateEntries
                                            .filter((entry) => entry.local?.id)
                                            .map((entry) => {
                                                const rawStatus = (entry.meta?.status || entry.local?.status || '').toLowerCase();
                                                const approved = rawStatus === 'approved';
                                                return (
                                                    <MenuItem key={entry.local.id} value={entry.local.id} disabled={!approved}>
                                                        {entry.local.name} {entry.local.language ? `(${entry.local.language})` : ''}
                                                        {!approved && ` — ${rawStatus || 'status desconhecido'}`}
                                                    </MenuItem>
                                                );
                                            })}
                                        {campaignForm.integration_id &&
                                            !loadingCampaignTemplates &&
                                            campaignTemplateEntries.filter((entry) => entry.local?.id).length === 0 && (
                                                <MenuItem disabled value="__none__">
                                                    Nenhum template cadastrado pra essa integração - crie um ou importe da Meta na tela
                                                    "Templates WhatsApp"
                                                </MenuItem>
                                            )}
                                    </TextField>
                                </Grid>
                            )}

                            {campaignForm.channel === 'whatsapp' &&
                                selectedCampaignTemplateEntry &&
                                (campaignHeaderVarNames.length > 0 || campaignBodyVarNames.length > 0) && (
                                    <Grid item xs={12}>
                                        <Alert severity="info">
                                            Esse template tem variável ({[...campaignHeaderVarNames, ...campaignBodyVarNames].join(', ')}) -
                                            preencha um valor pra cada destinatário logo abaixo, ao escolher quem vai receber o disparo.
                                        </Alert>
                                    </Grid>
                                )}

                            {campaignForm.channel === 'email' && (
                                <>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Assunto"
                                            value={campaignForm.email_subject}
                                            onChange={(e) => setCampaignForm({ ...campaignForm, email_subject: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={5}
                                            label="Mensagem"
                                            helperText="Use {{nome}} pra inserir o nome de cada destinatário (ex: Olá {{nome}}, tudo bem?)"
                                            value={campaignForm.email_message}
                                            onChange={(e) => setCampaignForm({ ...campaignForm, email_message: e.target.value })}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button variant="outlined" component="label" startIcon={<IconUpload size={18} />}>
                                            {campaignForm.email_image_file ? campaignForm.email_image_file.name : 'Adicionar imagem (opcional)'}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    if (campaignForm.email_image_preview_url) {
                                                        URL.revokeObjectURL(campaignForm.email_image_preview_url);
                                                    }
                                                    setCampaignForm({
                                                        ...campaignForm,
                                                        email_image_file: file,
                                                        email_image_preview_url: URL.createObjectURL(file)
                                                    });
                                                }}
                                            />
                                        </Button>
                                        {campaignForm.email_image_file && (
                                            <Button
                                                size="small"
                                                color="error"
                                                sx={{ ml: 1 }}
                                                onClick={() => {
                                                    if (campaignForm.email_image_preview_url) {
                                                        URL.revokeObjectURL(campaignForm.email_image_preview_url);
                                                    }
                                                    setCampaignForm({ ...campaignForm, email_image_file: null, email_image_preview_url: '' });
                                                }}
                                            >
                                                Remover
                                            </Button>
                                        )}
                                        {campaignForm.email_image_preview_url && (
                                            <Box sx={{ mt: 1.5 }}>
                                                <img
                                                    src={campaignForm.email_image_preview_url}
                                                    alt="Prévia da imagem do e-mail"
                                                    style={{ maxWidth: 240, maxHeight: 160, borderRadius: 8, display: 'block' }}
                                                />
                                            </Box>
                                        )}
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Link (opcional)"
                                            placeholder="https://..."
                                            value={campaignForm.email_link_url}
                                            onChange={(e) => setCampaignForm({ ...campaignForm, email_link_url: e.target.value })}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Texto do botão do link"
                                            placeholder="Saiba mais"
                                            disabled={!campaignForm.email_link_url}
                                            value={campaignForm.email_link_text}
                                            onChange={(e) => setCampaignForm({ ...campaignForm, email_link_text: e.target.value })}
                                        />
                                    </Grid>
                                </>
                            )}

                            {/* USERS */}

                            <Grid item xs={12}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 800,
                                        mb: 2
                                    }}
                                >
                                    Destinatários
                                </Typography>

                                <Tabs value={recipientMode} onChange={(_, value) => setRecipientMode(value)}>
                                    <Tab value="sistema" label="Contatos do sistema" />
                                    <Tab value="csv" label="Planilha CSV" />
                                </Tabs>

                                {recipientMode === 'sistema' && (
                                    <>
                                        <OutlinedInput
                                            fullWidth
                                            placeholder="Buscar usuários..."
                                            value={userSearch}
                                            onChange={(e) => setUserSearch(e.target.value)}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <IconSearch size={18} />
                                                </InputAdornment>
                                            }
                                            sx={{
                                                mb: 2,
                                                mt: 2,
                                                borderRadius: '16px'
                                            }}
                                        />

                                        <Paper
                                            sx={{
                                                maxHeight: 400,
                                                overflow: 'auto',
                                                borderRadius: '20px'
                                            }}
                                        >
                                            <List>
                                                {filteredUsers.map((user) => {
                                                    const selected = campaignForm.recipients.find((u) => u.id === user.id);
                                                    const vars = recipientVariables[user.id];
                                                    const mostrarVariaveis =
                                                        !!selected &&
                                                        (campaignHeaderVarNames.length > 0 || campaignBodyVarNames.length > 0);

                                                    return (
                                                        <Box key={user.id}>
                                                            <ListItem
                                                                secondaryAction={
                                                                    <Switch
                                                                        checked={!!selected}
                                                                        onChange={() => handleToggleRecipient(user)}
                                                                    />
                                                                }
                                                            >
                                                                <ListItemAvatar>
                                                                    <Avatar src={user.avatar}>{user.name?.charAt(0)}</Avatar>
                                                                </ListItemAvatar>

                                                                <ListItemText primary={user.name} secondary={user.email} />
                                                            </ListItem>

                                                            {mostrarVariaveis && (
                                                                <Box sx={{ px: 2, pb: 2, pl: 9 }}>
                                                                    <Grid container spacing={1}>
                                                                        {campaignHeaderVarNames.length > 0 && (
                                                                            <Grid item xs={12} sm={6}>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    size="small"
                                                                                    label={campaignHeaderVarNames[0]}
                                                                                    value={vars?.header || ''}
                                                                                    onChange={(e) =>
                                                                                        updateRecipientHeaderVar(user.id, e.target.value)
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                        )}
                                                                        {campaignBodyVarNames.map((varName, i) => (
                                                                            <Grid item xs={12} sm={6} key={i}>
                                                                                <TextField
                                                                                    fullWidth
                                                                                    size="small"
                                                                                    label={varName}
                                                                                    value={vars?.body?.[i] || ''}
                                                                                    onChange={(e) =>
                                                                                        updateRecipientBodyVar(user.id, i, e.target.value)
                                                                                    }
                                                                                />
                                                                            </Grid>
                                                                        ))}
                                                                    </Grid>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    );
                                                })}
                                            </List>
                                        </Paper>
                                    </>
                                )}

                                {recipientMode === 'csv' && (
                                    <Stack direction="row" spacing={1} flexWrap="wrap">
                                        <Button
                                            startIcon={<CloudDownloadIcon />}
                                            disabled={campaignForm.channel === 'whatsapp' && !selectedCampaignTemplateEntry}
                                            onClick={handleDownloadCampaignTemplate}
                                        >
                                            Baixar planilha modelo
                                        </Button>
                                        <Button component="label" startIcon={<IconFileSpreadsheet />}>
                                            Importar planilha
                                            <input hidden type="file" accept=".xlsx,.xls,.csv" onChange={handleCsvUpload} />
                                        </Button>
                                    </Stack>
                                )}
                            </Grid>

                            {recipientMode === 'csv' && campaignForm.channel === 'whatsapp' && !selectedCampaignTemplateEntry && (
                                <Grid item xs={12}>
                                    <Alert severity="info" sx={{ borderRadius: '12px', mt: 1 }}>
                                        Selecione um template acima pra baixar a planilha-modelo já com as colunas de variável certas.
                                    </Alert>
                                </Grid>
                            )}

                            {csvHeaders.length > 0 && (
                                <Grid container spacing={2} mt={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            select
                                            label="Coluna Nome"
                                            value={columnMapping.name}
                                            onChange={(e) =>
                                                setColumnMapping({
                                                    ...columnMapping,
                                                    name: e.target.value
                                                })
                                            }
                                        >
                                            {csvHeaders.map((header) => (
                                                <MenuItem key={header} value={header}>
                                                    {header}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        {campaignForm.channel === 'email' ? (
                                            <TextField
                                                fullWidth
                                                select
                                                label="Coluna E-mail"
                                                value={columnMapping.email}
                                                onChange={(e) =>
                                                    setColumnMapping({
                                                        ...columnMapping,
                                                        email: e.target.value
                                                    })
                                                }
                                            >
                                                {csvHeaders.map((header) => (
                                                    <MenuItem key={header} value={header}>
                                                        {header}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        ) : (
                                            <TextField
                                                fullWidth
                                                select
                                                label="Coluna Telefone"
                                                value={columnMapping.phone}
                                                onChange={(e) =>
                                                    setColumnMapping({
                                                        ...columnMapping,
                                                        phone: e.target.value
                                                    })
                                                }
                                            >
                                                {csvHeaders.map((header) => (
                                                    <MenuItem key={header} value={header}>
                                                        {header}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        )}
                                    </Grid>
                                </Grid>
                            )}

                            {csvRows.length > 0 && (
                                <Paper sx={{ mt: 2, p: 2, maxHeight: 450, overflow: 'auto' }}>
                                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                        <Typography variant="h6">
                                            {campaignHeaderVarNames.length > 0 || campaignBodyVarNames.length > 0
                                                ? 'Destinatários da planilha e variáveis do template'
                                                : 'Prévia da planilha'}
                                        </Typography>
                                        {variablesResolvedFromFile && (
                                            <Chip size="small" color="success" label="Variáveis preenchidas pela planilha" />
                                        )}
                                    </Stack>

                                    {csvRows.map((row, index) => {
                                        const isEmailCampaign = campaignForm.channel === 'email';
                                        const phone = columnMapping.phone
                                            ? normalizePossibleScientificNotation(row[columnMapping.phone])
                                            : '';
                                        const email = columnMapping.email ? (row[columnMapping.email] || '').toString().trim() : '';
                                        const contato = isEmailCampaign ? email : phone;
                                        const nome = columnMapping.name ? row[columnMapping.name] : '';
                                        const vars = recipientVariables[index];
                                        const temVariaveis = campaignHeaderVarNames.length > 0 || campaignBodyVarNames.length > 0;
                                        const mostrarVariaveis = !!contato && temVariaveis && !variablesResolvedFromFile;
                                        const mostrarVariaveisResolvidas = !!contato && temVariaveis && variablesResolvedFromFile;

                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    py: 1,
                                                    borderBottom: index < csvRows.length - 1 ? '1px solid rgba(128,128,128,0.2)' : 'none'
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {nome || `Linha ${index + 1}`}
                                                    {contato ? ` — ${contato}` : ''}
                                                    {!contato && (
                                                        <Typography component="span" variant="body2" sx={{ color: 'error.main', ml: 1 }}>
                                                            {isEmailCampaign
                                                                ? '(sem e-mail - será ignorada)'
                                                                : '(sem telefone - será ignorada)'}
                                                        </Typography>
                                                    )}
                                                </Typography>

                                                {mostrarVariaveis && (
                                                    <Grid container spacing={1} sx={{ mt: 0.5 }}>
                                                        {campaignHeaderVarNames.length > 0 && (
                                                            <Grid item xs={12} sm={6}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    label={campaignHeaderVarNames[0]}
                                                                    value={vars?.header || ''}
                                                                    onChange={(e) => updateRecipientHeaderVar(index, e.target.value)}
                                                                />
                                                            </Grid>
                                                        )}
                                                        {campaignBodyVarNames.map((varName, i) => (
                                                            <Grid item xs={12} sm={6} key={i}>
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    label={varName}
                                                                    value={vars?.body?.[i] || ''}
                                                                    onChange={(e) => updateRecipientBodyVar(index, i, e.target.value)}
                                                                />
                                                            </Grid>
                                                        ))}
                                                    </Grid>
                                                )}

                                                {mostrarVariaveisResolvidas && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                        {[campaignHeaderVarNames.length > 0 ? vars?.header : null, ...(vars?.body || [])]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Paper>
                            )}

                            {/* CAPTURA DE RESPOSTA */}

                            <Grid item xs={12}>
                                <Divider sx={{ my: 1 }} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: campaignForm.capture_response ? 2 : 0
                                    }}
                                >
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                                            Capturar resposta do cliente?
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Registra o que o cliente responder (ex: clique num botão do template) e decide o que fazer com
                                            isso.
                                        </Typography>
                                    </Box>
                                    <Switch
                                        checked={campaignForm.capture_response}
                                        onChange={(e) => setCampaignForm({ ...campaignForm, capture_response: e.target.checked })}
                                    />
                                </Box>

                                {campaignForm.capture_response && (
                                    <Grid container spacing={2}>
                                        {!campaignForm.whatsapp_template_id && (
                                            <Grid item xs={12}>
                                                <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                                                    Selecione um template aprovado acima - a captura de resposta só funciona quando a
                                                    mensagem é enviada como Template Message oficial (só assim dá pra saber qual resposta
                                                    pertence a qual disparo).
                                                </Alert>
                                            </Grid>
                                        )}

                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                select
                                                label="O que fazer com a resposta"
                                                value={campaignForm.response_action_type}
                                                onChange={(e) => setCampaignForm({ ...campaignForm, response_action_type: e.target.value })}
                                            >
                                                {RESPONSE_ACTION_TYPES.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </TextField>
                                        </Grid>

                                        {campaignForm.response_action_type === 'opt_in_capture' && (
                                            <>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Texto do botão de opt-in"
                                                        placeholder="Quero receber promoções"
                                                        helperText="Precisa bater exatamente com o texto do botão configurado no template"
                                                        value={campaignForm.response_opt_in_text}
                                                        onChange={(e) =>
                                                            setCampaignForm({ ...campaignForm, response_opt_in_text: e.target.value })
                                                        }
                                                    />
                                                </Grid>
                                                <Grid item xs={12} md={6}>
                                                    <TextField
                                                        fullWidth
                                                        label="Texto do botão de opt-out"
                                                        placeholder="Parar de receber"
                                                        helperText="Precisa bater exatamente com o texto do botão configurado no template"
                                                        value={campaignForm.response_opt_out_text}
                                                        onChange={(e) =>
                                                            setCampaignForm({ ...campaignForm, response_opt_out_text: e.target.value })
                                                        }
                                                    />
                                                </Grid>
                                            </>
                                        )}

                                        {campaignForm.response_action_type === 'ai_agent' && (
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Agente de IA que deve responder"
                                                    value={campaignForm.response_ai_agent_webhook_url}
                                                    onChange={(e) =>
                                                        setCampaignForm({
                                                            ...campaignForm,
                                                            response_ai_agent_webhook_url: e.target.value
                                                        })
                                                    }
                                                >
                                                    <MenuItem value="">Selecione um agente</MenuItem>
                                                    {aiAgents.map((agent) => (
                                                        <MenuItem key={agent.id} value={agent.webhook_url} disabled={!agent.webhook_url}>
                                                            {agent.name} {!agent.webhook_url && '(sem webhook configurado)'}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        )}

                                        {campaignForm.response_action_type === 'human_queue' && (
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Fila humana de destino"
                                                    value={campaignForm.response_human_queue_id}
                                                    onChange={(e) =>
                                                        setCampaignForm({ ...campaignForm, response_human_queue_id: e.target.value })
                                                    }
                                                >
                                                    <MenuItem value="">Selecione uma fila</MenuItem>
                                                    {switchboardIntegrations.map((swb) => (
                                                        <MenuItem key={swb.id} value={swb.id}>
                                                            {swb.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                        )}
                                    </Grid>
                                )}
                            </Grid>

                            {/* SCHEDULE */}

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="date"
                                    label="Data do disparo"
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    value={campaignForm.schedule_date}
                                    onChange={(e) =>
                                        setCampaignForm({
                                            ...campaignForm,
                                            schedule_date: e.target.value
                                        })
                                    }
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    type="time"
                                    label="Hora do disparo"
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    value={campaignForm.schedule_time}
                                    onChange={(e) =>
                                        setCampaignForm({
                                            ...campaignForm,
                                            schedule_time: e.target.value
                                        })
                                    }
                                />
                            </Grid>

                            {/* DELAY ENTRE ENVIOS - só faz sentido pra WhatsApp (e-mail não tem
                                risco de bloqueio por rajada do jeito que WhatsApp tem). */}
                            {campaignForm.channel === 'whatsapp' && (
                                <>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Delay entre envios (segundos)"
                                            helperText={`Mínimo ${minDelaySeconds}s ${
                                                isUnofficialWhatsappCampaign ? '(API não-oficial)' : '(API Oficial)'
                                            } - quanto maior, menor o risco de bloqueio do número`}
                                            value={campaignForm.send_delay_seconds}
                                            onChange={(e) =>
                                                setCampaignForm({
                                                    ...campaignForm,
                                                    send_delay_seconds: e.target.value
                                                })
                                            }
                                            onBlur={() => {
                                                const valor = Number(campaignForm.send_delay_seconds);
                                                if (campaignForm.send_delay_seconds !== '' && valor < minDelaySeconds) {
                                                    setCampaignForm({ ...campaignForm, send_delay_seconds: String(minDelaySeconds) });
                                                }
                                            }}
                                            inputProps={{ min: minDelaySeconds }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {estimatedTotalSeconds
                                                ? `Tempo estimado do disparo: ~${formatEstimatedDuration(estimatedTotalSeconds)} (${recipientCount} destinatário(s))`
                                                : 'Selecione os destinatários pra ver a estimativa de tempo'}
                                        </Typography>
                                    </Grid>

                                    {isUnofficialWhatsappCampaign && (
                                        <Grid item xs={12}>
                                            <Alert severity={excedeuLimiteNaoOficial ? 'error' : 'info'}>
                                                {excedeuLimiteNaoOficial
                                                    ? `Limite de ${MAX_UNOFFICIAL_RECIPIENTS} destinatários por campanha na API não-oficial (você tem ${recipientCount}) - divida em campanhas menores ou use a API Oficial pra esse volume.`
                                                    : `API não-oficial: até ${MAX_UNOFFICIAL_RECIPIENTS} destinatários por campanha, um por vez com esse delay entre cada um - a Meta é sensível a disparo em rajada fora do app oficial dela.`}
                                            </Alert>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Box>

                    {/* RIGHT PREVIEW */}

                    <Box
                        sx={{
                            width: 420,
                            borderLeft:
                                theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(15,23,42,0.06)',
                            background: theme.palette.mode === 'dark' ? '#111827' : '#fff',
                            p: 3,
                            overflow: 'auto'
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 800,
                                mb: 3
                            }}
                        >
                            Pré-visualização
                        </Typography>

                        {status.type === 'error' && status.message && (
                            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setStatus({ type: '', message: '' })}>
                                {status.message}
                            </Alert>
                        )}

                        {(() => {
                            const selectedTemplate = campaignTemplateEntries.find(
                                (entry) => entry.local?.id === campaignForm.whatsapp_template_id
                            )?.local;
                            const selectedIntegration = channelIntegrations.find((i) => i.id === campaignForm.integration_id);
                            if (!selectedTemplate) {
                                return (
                                    <Paper sx={{ borderRadius: '28px', p: 4, maxWidth: 360, mx: 'auto', textAlign: 'center' }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {campaignForm.channel === 'whatsapp'
                                                ? 'Selecione a integração e o template pra ver a prévia'
                                                : 'Prévia disponível só pra campanhas de WhatsApp com template'}
                                        </Typography>
                                    </Paper>
                                );
                            }
                            return (
                                <Box sx={{ maxWidth: 360, mx: 'auto' }}>
                                    <TemplatePreviewCard
                                        appId={appId}
                                        entry={{ meta: null, local: selectedTemplate }}
                                        clientType={selectedIntegration?.client_type}
                                    />
                                </Box>
                            );
                        })()}

                        <Divider
                            sx={{
                                my: 4
                            }}
                        />

                        <Stack spacing={2}>
                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <IconClock size={18} />}
                                disabled={
                                    campaignSaveDisabled ||
                                    !campaignForm.schedule_date ||
                                    new Date(`${campaignForm.schedule_date}T${campaignForm.schedule_time || '00:00'}:00`) <= new Date()
                                }
                                onClick={() => handleSaveCampaign(false)}
                                sx={{
                                    height: 54,
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg,#f59e0b,#fb923c)'
                                }}
                            >
                                Agendar Disparo
                            </Button>

                            <Button
                                fullWidth
                                variant="contained"
                                startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <IconSend size={18} />}
                                disabled={campaignSaveDisabled}
                                onClick={() => handleSaveCampaign(true)}
                                sx={{
                                    height: 54,
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    fontWeight: 700,
                                    background: 'linear-gradient(135deg,#2563eb,#7c3aed)'
                                }}
                            >
                                Salvar Campanha
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Dialog>

            <CampaignDetailDialog
                open={!!detailCampaignId}
                onClose={() => setDetailCampaignId(null)}
                appId={appId}
                campaignId={detailCampaignId}
            />
        </Box>
    );
};

export default MarketingCampaigns;
